import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

export interface AlertField {
  label: string;
  value: React.ReactNode;
  /** Color for the value (e.g. warning amount). */
  color?: string;
  mono?: boolean;
  /** Spans the whole row on phones (addresses). */
  wide?: boolean;
}

interface AlertShellProps {
  color: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  fields?: AlertField[];
  /** Right-hand slot: status chip and/or actions. */
  trailing?: React.ReactNode;
  children?: React.ReactNode;
  /** Decorative overlay (e.g. shimmer). */
  overlay?: object;
}

/**
 * Operational alert row: identity on the left, key figures in the middle,
 * status/actions on the right. Collapses into a stacked card on phones.
 */
export const AlertShell: React.FC<AlertShellProps> = ({
  color,
  icon,
  title,
  subtitle,
  fields = [],
  trailing,
  children,
  overlay,
}) => (
  <Box
    role="status"
    sx={{
      containerType: "inline-size",
      position: "relative",
      overflow: "hidden",
      isolation: "isolate",
      borderRadius: { xs: "24px", md: "28px" },
      p: { xs: 2.5, md: 3 },
      py: { md: 2.5 },
      background: `linear-gradient(90deg, ${alpha(color, 0.15)} 0%, ${alpha(color, 0.05)} 45%, rgba(255,255,255,0.02) 100%)`,
      border: `1px solid ${alpha(color, 0.32)}`,
      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06), 0 24px 48px -28px rgba(0,0,0,0.9)`,
      backdropFilter: { xs: "none", md: "blur(18px) saturate(140%)" },
      WebkitBackdropFilter: { xs: "none", md: "blur(18px) saturate(140%)" },
      ...(overlay ? { "&::before": overlay } : {}),
    }}
  >
    <Box
      sx={{
        position: "relative",
        display: "grid",
        alignItems: "center",
        columnGap: 3,
        rowGap: 2,
        // Layout follows the alert's own width (container query), not the viewport:
        // stacked on phones, two rows on tablets, one row only when everything fits.
        gridTemplateColumns: "minmax(0, 1fr)",
        gridTemplateAreas: trailing ? `"id" "fields" "trail"` : `"id" "fields"`,
        "@container (min-width: 600px)": trailing
          ? { gridTemplateColumns: "minmax(0, 1fr) auto", gridTemplateAreas: `"id trail" "fields fields"` }
          : {},
        "@container (min-width: 1000px)": {
          gridTemplateColumns: trailing ? "minmax(220px, 280px) minmax(0, 1fr) auto" : "minmax(220px, 280px) minmax(0, 1fr)",
          gridTemplateAreas: trailing ? `"id fields trail"` : `"id fields"`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ gridArea: "id", minWidth: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(color, 0.2),
            border: `1px solid ${alpha(color, 0.28)}`,
            boxShadow: `0 0 18px -4px ${alpha(color, 0.45)}`,
            color,
            flexShrink: 0,
            "& svg": { fontSize: 21 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: "text.primary", letterSpacing: "-0.005em" }} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              noWrap
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {(fields.length > 0 || children) && (
        <Box sx={{ gridArea: "fields", minWidth: 0 }}>
          {fields.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                columnGap: 2,
                rowGap: 1.5,
                // Wider alerts: values sit side by side at their natural width and wrap if needed.
                "@container (min-width: 600px)": {
                  display: "flex",
                  flexWrap: "wrap",
                  columnGap: 4,
                },
              }}
            >
              {fields.map((f) => (
                <Box
                  key={f.label}
                  sx={{
                    minWidth: 0,
                    gridColumn: f.wide ? "1 / -1" : undefined,
                    "@container (min-width: 600px)": { flex: "0 0 auto" },
                  }}
                >
                  <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    {f.label}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 650,
                      color: f.color ?? "text.primary",
                      fontFamily: f.mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
                      fontVariantNumeric: "tabular-nums",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.value}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {children}
        </Box>
      )}

      {trailing && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ gridArea: "trail", justifyContent: "flex-start", "@container (min-width: 600px)": { justifyContent: "flex-end" } }}
        >
          {trailing}
        </Stack>
      )}
    </Box>
  </Box>
);

export default AlertShell;
